import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Flame, Phone, ShieldAlert, Users } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { telHref } from "@/lib/phone";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// National fallback — used for any service the city desk hasn't set up its own number for yet.
const NATIONAL_NUMBERS = { police: "112", fire: "101", women: "1091", child: "1098" };

// city_contacts has no dedicated "kind" field (it's a free-form emergency/utility/civic list), so a
// service-specific number is found by keyword match on the operator's own label. Falls back to the
// national number when nothing matches — never blank.
const KEYWORDS = {
  police: ["police", "पुलिस"],
  fire: ["fire", "फायर", "दमकल"],
  women: ["women", "महिला"],
  child: ["child", "बाल", "बच्च"],
};

function numberFor(contacts, service) {
  const hit = contacts.find((c) =>
    KEYWORDS[service].some((kw) => `${c.label} ${c.label_hi}`.toLowerCase().includes(kw.toLowerCase())),
  );
  return hit?.phone || NATIONAL_NUMBERS[service];
}

export function EmergencyDialog({ open, onOpenChange }) {
  const { t, tenant } = useApp();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!open || !tenant) return;
    api.get("/city/contacts", { params: { tenant_id: tenant.id } }).then((r) => setContacts(r.data));
  }, [open, tenant]);

  const rows = [
    { service: "police", icon: ShieldAlert },
    { service: "fire", icon: Flame },
    { service: "women", icon: Users },
    { service: "child", icon: Users },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="emergency-dialog" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle size={18} /> {t("emergency.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5">
          <button
            data-testid="emergency-ambulance-row"
            onClick={() => {
              onOpenChange(false);
              navigate("/ambulance");
            }}
            className="flex min-h-[52px] w-full items-center gap-3 rounded-xl px-2 text-left transition-colors duration-200 hover:bg-muted"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive">
              <Phone size={16} />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-semibold">{t("emergency.ambulance")}</span>
              <span className="block text-xs text-muted-foreground">{t("emergency.ambulanceHint")}</span>
            </span>
          </button>

          {rows.map(({ service, icon: Icon }) => {
            const number = numberFor(contacts, service);
            return (
              <a
                key={service}
                href={telHref(number) ?? undefined}
                data-testid={`emergency-${service}-row`}
                aria-label={`${t(`emergency.${service}`)}, ${number}`}
                className="flex min-h-[52px] w-full items-center gap-3 rounded-xl px-2 text-left transition-colors duration-200 hover:bg-muted"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive">
                  <Icon size={16} />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold">{t(`emergency.${service}`)}</span>
                  <span className="block text-xs text-muted-foreground">{t(`emergency.${service}Hint`)}</span>
                </span>
                <span className="font-mono text-sm font-bold text-destructive">{number}</span>
              </a>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
